/** A conversation's `/remind` plumbing: reminders go through the workspace's Later, and the answer shows as a toast. */
import type { ChannelViewModel, CommandNotice } from "@bundle/core";
import { Toast } from "../primitives/index.ts";
import { useFormat, useT, useTr } from "../i18n/I18nProvider.tsx";
import { useEffect } from "react";
import { useEmojiUsage, useLater, useScheduled } from "./registry.ts";

export function CommandNoticeToast({ vm, notice }: { vm: ChannelViewModel | undefined; notice: CommandNotice | undefined }) {
  const t = useT();
  const tr = useTr();
  const fmt = useFormat();
  const later = useLater();
  const scheduled = useScheduled();
  const emojiUsage = useEmojiUsage();
  useEffect(() => {
    vm?.setEmojiSink(emojiUsage === undefined ? undefined : (emoji) => emojiUsage.pick(emoji));
    return () => vm?.setEmojiSink(undefined);
  }, [vm, emojiUsage]);
  useEffect(() => {
    vm?.setReminderSink(later === undefined ? undefined : (text, at, opts) => later.addTextReminder(text, at, opts));
    return () => vm?.setReminderSink(undefined);
  }, [vm, later]);
  useEffect(() => {
    vm?.setScheduleSink(scheduled === undefined ? undefined : (req) => scheduled.schedule(req));
    return () => vm?.setScheduleSink(undefined);
  }, [vm, scheduled]);
  const message =
    notice === undefined
      ? ""
      : notice.kind === "reminded"
        ? notice.repeats === true
          ? t("composer.remind.doneRepeat", { who: notice.who ?? t("composer.remind.you"), text: notice.text, when: fmt.timestamp(notice.at) })
          : notice.who !== undefined
            ? t("composer.remind.doneFor", { who: notice.who, text: notice.text, when: fmt.timestamp(notice.at) })
            : t("composer.remind.done", { text: notice.text, when: fmt.timestamp(notice.at) })
        : notice.kind === "scheduled"
          ? t("schedule.done", { when: fmt.timestamp(notice.at) })
          : tr(notice.message);
  return <Toast open={notice !== undefined} onClose={() => vm?.dismissCommandNotice()} message={message} closeLabel={t("common.close")} durationMs={6000} testID="command-notice" />;
}
